'use client'

import { useState } from 'react'
import Link from 'next/link'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import { useUserStore } from '@/store/user-store'
import {
  Users, Gift, Clock, Flame, Zap, Crown, Heart,
  ChevronRight, Share2, MessageCircle, Star, Trophy, Tag
} from 'lucide-react'
import { toast } from 'sonner'

interface WelfareActivity {
  id: string
  title: string
  description: string
  image: string
  tag: string
  tagColor: string
  endDate: string
  participants: number
}

interface GroupBuyItem {
  id: string
  title: string
  originalPrice: number
  groupPrice: number
  currentCount: number
  targetCount: number
  image: string
  endTime: string
}

const WELFARE_ACTIVITIES: WelfareActivity[] = [
  {
    id: 'w1', title: '新人专属·首单立减15元', description: '新注册用户首次下单即享优惠，全场蛋糕可用',
    image: '🎉', tag: '新人', tagColor: 'bg-green-500',
    endDate: '2026-12-31', participants: 386,
  },
  {
    id: 'w2', title: '会员日·每周三8折特惠', description: '银卡及以上会员每周三可享全场8折优惠',
    image: '👑', tag: '会员', tagColor: 'bg-purple-500',
    endDate: '2026-12-31', participants: 1523,
  },
  {
    id: 'w3', title: '邀请好友·各得20元券', description: '每邀请1位好友注册，双方各获得20元优惠券',
    image: '🤝', tag: '邀请', tagColor: 'bg-blue-500',
    endDate: '2026-12-31', participants: 628,
  },
  {
    id: 'w4', title: '生日月·免费6寸蛋糕', description: '金卡及以上会员生日当月可免费领取6寸蛋糕',
    image: '🎂', tag: '生日', tagColor: 'bg-pink-500',
    endDate: '2026-12-31', participants: 95,
  },
  {
    id: 'w5', title: '积分抽奖·100%中奖', description: '消耗50积分即可参与抽奖，最高赢取免费蛋糕',
    image: '🎰', tag: '抽奖', tagColor: 'bg-amber-500',
    endDate: '2026-06-30', participants: 2451,
  },
  {
    id: 'w6', title: '节日限定·母亲节特惠', description: '母亲节期间指定蛋糕享买一送一优惠',
    image: '💐', tag: '节日', tagColor: 'bg-rose-500',
    endDate: '2026-05-12', participants: 0,
  },
]

const GROUP_BUY_ITEMS: GroupBuyItem[] = [
  {
    id: 'g1', title: '经典黑森林蛋糕', originalPrice: 168, groupPrice: 128,
    currentCount: 8, targetCount: 10, image: '🍫', endTime: '2026-04-25',
  },
  {
    id: 'g2', title: '鲜果缤纷蛋糕', originalPrice: 198, groupPrice: 158,
    currentCount: 5, targetCount: 10, image: '🍓', endTime: '2026-04-25',
  },
  {
    id: 'g3', title: '抹茶千层蛋糕', originalPrice: 158, groupPrice: 118,
    currentCount: 12, targetCount: 15, image: '🍵', endTime: '2026-04-26',
  },
]

const GROUP_CHAT_LIST = [
  { id: 'gc1', name: '蛋糕新品试吃群', members: 328, desc: '第一时间获取新品资讯', tag: '活跃' },
  { id: 'gc2', name: '妈妈烘焙交流群', members: 586, desc: '烘焙技巧分享·亲子DIY', tag: '热门' },
  { id: 'gc3', name: '企业团购福利群', members: 156, desc: '企业团购专属优惠', tag: '团购' },
  { id: 'gc4', name: '甜品爱好者联盟', members: 423, desc: '甜品推荐·打卡分享', tag: '推荐' },
]

export default function CommunityPage() {
  const { user } = useUserStore()
  const [showQR, setShowQR] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState<'welfare' | 'groupbuy' | 'groups'>('welfare')

  const handleJoinGroup = (groupId: string) => {
    setShowQR(groupId)
  }

  const handleShare = () => {
    const shareText = `我在「甜蜜时光蛋糕」发现了超值的蛋糕团购，快来看看吧！`
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareText)
      toast.success('分享文案已复制到剪贴板')
    } else {
      toast.info('请手动分享给好友')
    }
  }

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar title="社群福利" />

      {/* 入口头部 */}
      <div className="mx-4 mt-3 p-4 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl text-white relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-20 h-20 bg-white/10 rounded-full" />
        <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-white/10 rounded-full" />

        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-5 h-5" />
            <span className="text-lg font-bold">社群专区</span>
          </div>
          <p className="text-xs opacity-80 mb-4">加入社群获取专属优惠、新品试吃、拼团福利</p>

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white/15 rounded-xl p-2.5 text-center">
              <p className="text-lg font-bold">1.5k+</p>
              <p className="text-[10px] opacity-70">社群成员</p>
            </div>
            <div className="bg-white/15 rounded-xl p-2.5 text-center">
              <p className="text-lg font-bold">20+</p>
              <p className="text-[10px] opacity-70">福利活动</p>
            </div>
            <div className="bg-white/15 rounded-xl p-2.5 text-center">
              <p className="text-lg font-bold">¥5k+</p>
              <p className="text-[10px] opacity-70">已省金额</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab 切换 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-1 flex gap-1">
        {[
          { key: 'welfare', label: '福利活动' },
          { key: 'groupbuy', label: '拼团特惠' },
          { key: 'groups', label: '社群列表' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveSection(tab.key as any)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
              activeSection === tab.key ? 'bg-violet-500 text-white' : 'text-gray-500'
            }`}
          >{tab.label}</button>
        ))}
      </div>

      {/* 福利活动 */}
      {activeSection === 'welfare' && (
        <div className="mx-4 mt-3 space-y-2 mb-4">
          {WELFARE_ACTIVITIES.map(activity => (
            <div key={activity.id} className="bg-white rounded-xl p-3 flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-50 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                {activity.image}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 ${activity.tagColor} text-white text-[8px] rounded`}>{activity.tag}</span>
                  <span className="text-sm font-bold text-gray-800 truncate">{activity.title}</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5 truncate">{activity.description}</p>
                {activity.participants > 0 && (
                  <p className="text-[10px] text-gray-400 mt-0.5">{activity.participants}人已参与</p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
            </div>
          ))}
        </div>
      )}

      {/* 拼团特惠 */}
      {activeSection === 'groupbuy' && (
        <div className="mx-4 mt-3 space-y-2 mb-4">
          {GROUP_BUY_ITEMS.map(item => {
            const progress = (item.currentCount / item.targetCount) * 100
            const isComplete = item.currentCount >= item.targetCount
            return (
              <div key={item.id} className="bg-white rounded-xl overflow-hidden">
                <div className="p-3 flex items-center gap-3">
                  <div className="w-16 h-16 bg-gray-50 rounded-xl flex items-center justify-center text-3xl flex-shrink-0">
                    {item.image}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800">{item.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm font-bold text-pink-500">¥{item.groupPrice}</span>
                      <span className="text-xs text-gray-400 line-through">¥{item.originalPrice}</span>
                      <span className="px-1 py-0.5 bg-red-100 text-red-500 text-[8px] rounded">省¥{item.originalPrice - item.groupPrice}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${isComplete ? 'bg-green-500' : 'bg-pink-500'}`}
                          style={{ width: `${Math.min(100, progress)}%` }} />
                      </div>
                      <span className="text-[10px] text-gray-400 flex-shrink-0">
                        {item.currentCount}/{item.targetCount}人
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex border-t border-gray-50">
                  <button
                    onClick={handleShare}
                    className="flex-1 py-2.5 text-xs text-gray-500 flex items-center justify-center gap-1 border-r border-gray-50"
                  >
                    <Share2 className="w-3.5 h-3.5" /> 邀请拼团
                  </button>
                  <button
                    onClick={() => toast.success('已加入拼团！')}
                    className={`flex-1 py-2.5 text-xs font-bold flex items-center justify-center gap-1 ${
                      isComplete ? 'text-green-500' : 'text-pink-500'
                    }`}
                  >
                    {isComplete ? <><Check className="w-3.5 h-3.5" />已成团</> : <><Zap className="w-3.5 h-3.5" />立即参团</>}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 社群列表 */}
      {activeSection === 'groups' && (
        <div className="mx-4 mt-3 space-y-2 mb-4">
          {GROUP_CHAT_LIST.map(group => (
            <div key={group.id} className="bg-white rounded-xl p-3 flex items-center gap-3">
              <div className="w-11 h-11 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-5 h-5 text-violet-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-bold text-gray-800">{group.name}</span>
                  <span className="px-1 py-0.5 bg-violet-100 text-violet-500 text-[8px] rounded">{group.tag}</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">{group.desc} · {group.members}人</p>
              </div>
              <button
                onClick={() => handleJoinGroup(group.id)}
                className="px-3 py-1.5 bg-violet-500 text-white text-[10px] font-bold rounded-full flex-shrink-0"
              >
                加入
              </button>
            </div>
          ))}
        </div>
      )}

      <BottomNav />

      {/* 加入群二维码弹窗 */}
      {showQR && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={() => setShowQR(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-xs text-center" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-gray-800 mb-1">扫码加入社群</h3>
            <p className="text-[10px] text-gray-400 mb-3">长按识别或截图保存</p>
            <div className="w-48 h-48 mx-auto bg-gray-100 rounded-xl flex flex-col items-center justify-center mb-3">
              <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-xs text-gray-400">社群二维码</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">加入社群享受专属优惠和最新活动通知</p>
            <button onClick={() => setShowQR(null)} className="w-full py-2 bg-gray-100 text-gray-600 text-sm rounded-full">关闭</button>
          </div>
        </div>
      )}
    </div>
  )
}
